
const VERSION = 1;
const PREFIX = "enc:v1:";
const KDF_ITERATIONS = 600_000;

const encoder = new TextEncoder();
const decoder = new TextDecoder();

function toBase64(bytes) {
  let binary = "";

  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode(
      ...bytes.subarray(i, i + 8192)
    );
  }

  return btoa(binary);
}

function fromBase64(value) {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}

function validateClientId(clientId) {
  if (
    clientId === null ||
    clientId === undefined ||
    String(clientId).trim() === ""
  ) {
    throw new Error("A valid client ID is required.");
  }
}

async function deriveKey(passphrase, salt) {
  if (
    typeof passphrase !== "string" ||
    passphrase.length < 12
  ) {
    throw new Error(
      "Encryption passphrase must contain at least 12 characters."
    );
  }

  const material = await crypto.subtle.importKey(
    "raw",
    encoder.encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: KDF_ITERATIONS,
      hash: "SHA-256"
    },
    material,
    {
      name: "AES-GCM",
      length: 256
    },
    false,
    ["encrypt", "decrypt"]
  );
}

function keyContext(clientId) {
  return encoder.encode(
    `WheelsERP|key|${String(clientId)}|v${VERSION}`
  );
}

function fieldContext({ clientId, table, column }) {
  validateClientId(clientId);

  if (
    typeof table !== "string" ||
    !table.trim() ||
    typeof column !== "string" ||
    !column.trim()
  ) {
    throw new Error(
      "Table and column are required for encryption."
    );
  }

  return encoder.encode(
    `WheelsERP|${String(clientId)}|${table}|${column}|v${VERSION}`
  );
}

/**
 * Create a new random encryption key and a wrapped bundle.
 *
 * Store only the bundle on the server.
 * Keep the returned CryptoKey on the authorized client.
 */
export async function createEncryptionKey(
  passphrase,
  clientId
) {
  validateClientId(clientId);

  const salt = crypto.getRandomValues(
    new Uint8Array(16)
  );

  const nonce = crypto.getRandomValues(
    new Uint8Array(12)
  );

  const rawKey = crypto.getRandomValues(
    new Uint8Array(32)
  );

  try {
    const wrappingKey = await deriveKey(
      passphrase,
      salt
    );

    const wrapped = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: nonce,
        additionalData: keyContext(clientId)
      },
      wrappingKey,
      rawKey
    );

    const encryptionKey = await crypto.subtle.importKey(
      "raw",
      rawKey,
      { name: "AES-GCM" },
      false,
      ["encrypt", "decrypt"]
    );

    const bundle = {
      version: VERSION,
      iterations: KDF_ITERATIONS,
      clientId: String(clientId),
      salt: toBase64(salt),
      nonce: toBase64(nonce),
      wrappedKey: toBase64(
        new Uint8Array(wrapped)
      )
    };

    return {
      encryptionKey,
      bundle
    };
  } finally {
    rawKey.fill(0);
  }
}

/**
 * Unlock an existing encryption key bundle.
 */
export async function unlockEncryptionKey(
  passphrase,
  bundle,
  clientId
) {
  validateClientId(clientId);

  if (
    !bundle ||
    bundle.version !== VERSION ||
    bundle.iterations !== KDF_ITERATIONS ||
    String(bundle.clientId) !== String(clientId)
  ) {
    throw new Error(
      "Invalid encryption bundle or client ID."
    );
  }

  const salt = fromBase64(bundle.salt);
  const nonce = fromBase64(bundle.nonce);
  const wrappedKey = fromBase64(bundle.wrappedKey);

  if (salt.length !== 16 || nonce.length !== 12) {
    throw new Error("Invalid encryption bundle.");
  }

  const wrappingKey = await deriveKey(
    passphrase,
    salt
  );

  let rawKey;

  try {
    rawKey = new Uint8Array(
      await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: nonce,
          additionalData: keyContext(clientId)
        },
        wrappingKey,
        wrappedKey
      )
    );

    if (rawKey.length !== 32) {
      throw new Error("Invalid encryption key.");
    }

    return await crypto.subtle.importKey(
      "raw",
      rawKey,
      { name: "AES-GCM" },
      false,
      ["encrypt", "decrypt"]
    );
  } catch {
    throw new Error(
      "Cannot unlock the key. Check the passphrase and bundle."
    );
  } finally {
    rawKey?.fill(0);
  }
}

/**
 * Encrypt a field value.
 *
 * context example:
 * { clientId: 1, table: "customers", column: "customername" }
 */
export async function encryptValue(
  value,
  encryptionKey,
  context
) {
  if (value === null || value === undefined || value === "") {
    return value ?? null;
  }

  if (!encryptionKey) {
    throw new Error("Encryption key is not unlocked.");
  }

  const iv = crypto.getRandomValues(
    new Uint8Array(12)
  );

  const encrypted = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
      additionalData: fieldContext(context)
    },
    encryptionKey,
    encoder.encode(String(value))
  );

  return PREFIX + JSON.stringify({
    iv: toBase64(iv),
    data: toBase64(new Uint8Array(encrypted))
  });
}

/**
 * Decrypt a field value.
 */
export async function decryptValue(
  value,
  encryptionKey,
  context
) {
  if (value === null || value === undefined || value === "") {
    return value ?? null;
  }

  if (
    typeof value !== "string" ||
    !value.startsWith(PREFIX)
  ) {
    throw new Error(
      `Field "${context?.column}" is not encrypted.`
    );
  }

  if (!encryptionKey) {
    throw new Error("Encryption key is not unlocked.");
  }

  try {
    const payload = JSON.parse(
      value.slice(PREFIX.length)
    );

    const iv = fromBase64(payload.iv);
    const data = fromBase64(payload.data);

    if (iv.length !== 12) {
      throw new Error("Invalid encrypted value.");
    }

    const decrypted = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv,
        additionalData: fieldContext(context)
      },
      encryptionKey,
      data
    );

    return decoder.decode(decrypted);
  } catch {
    throw new Error(
      `Unable to decrypt field "${context?.column}".`
    );
  }
}

/**
 * Check whether a field uses this encryption format.
 */
export function isEncrypted(value) {
  return (
    typeof value === "string" &&
    value.startsWith(PREFIX)
  );
}
